import jwtDecode from 'jwt-decode';
import axios from 'axios';
import { dispatch } from 'redux/store';
import { AuthIdToken } from '../types';
import { useAuthContext } from 'identityAndAccess/infrastructure/useAuthContext';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { User, getUser, useGetUsers } from 'api-client';
import { UserEntity } from '@planitgeo/treeplotter-domain/lib/modules/identity-and-access/user/domain/user.model';
import { externalLogout, isTokenExpirationValid } from '../utils';
import { useEffect, useState, useContext, useMemo } from 'react';
import { saveUsers } from 'identityAndAccess/infrastructure/user/redux';
import TenantRepository from 'common/infrastructure/tenant/repository';
import { OnlineStatusContext } from 'common/infrastructure/network/onlineStatus/OnlineStatusContext';

const UserRepository = {
  useCurrentUser(isAuthenticated: boolean) {
    const [data, setData] = useState<User | undefined>(undefined);
    // Safely get online status - may be null if not inside OnlineStatusProvider
    const onlineStatusContext = useContext(OnlineStatusContext);
    const isOnline = onlineStatusContext?.isOnline ?? navigator.onLine;

    useEffect(() => {
      const fetchUser = async () => {
        try {
          const idToken = localStorage.getItem('idToken');
          const decodedIdToken = jwtDecode<AuthIdToken>(idToken as string);

          const response = await getUser({ userId: decodedIdToken.sub });
          setData(response);
        } catch (error) {
          loggerService.log(LoggerLevel.ERROR, 'User could not be retrieved', error);

          if (axios.isAxiosError(error) && error.response?.status === 401) {
            const idToken = localStorage.getItem('idToken');
            const tokenValid = idToken ? isTokenExpirationValid(idToken) : false;
            loggerService.log(
              LoggerLevel.DEBUG,
              '[LOGOUT_TRIGGER] 401 error in user repository - triggering logout',
              {
                userId: idToken ? jwtDecode<AuthIdToken>(idToken).sub : 'unknown',
                hasToken: !!idToken,
                tokenValid,
                errorStatus: error.response?.status,
                errorData: error.response?.data,
              }
            );
            await externalLogout({ skipLogoutWhenOffline: true });
            return;
          }
          // For non-401/transient errors (including offline), do not logout and do not clear last known user
        }
      };

      if (isAuthenticated && isOnline) {
        void fetchUser();
      }
    }, [isAuthenticated, isOnline]);

    return data;
  },

  useUsers() {
    const { isAuthenticated } = useAuthContext();
    const tenantId = TenantRepository.getCurrentTenantPid();

    const useQueryResponse = useGetUsers(
      tenantId,
      {
        tenantId,
      },
      {
        query: {
          enabled: isAuthenticated && !!tenantId,
        },
      }
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = useQueryResponse?.data;
    const users: UserEntity[] = useMemo(() => data ?? [], [data]);

    // Save users to Redux in an effect to avoid updating state during render
    useEffect(() => {
      if (users.length > 0) {
        UserRepository.saveUsers(users);
      }
    }, [users]);

    return {
      ...useQueryResponse,
      users,
    };
  },

  saveUsers(users: UserEntity[]) {
    dispatch(saveUsers(users));
  },
};

export { UserRepository as default };
